import {postData} from "@/utils/sendData";
import {API} from "@/api/API";
import {deserializedData} from "@/utils/deserializedData";
import camelcaseKeys from "camelcase-keys";
import {serializeFormValues} from "@/views/Admin/Events/Event/Promocodes/Form/Form.methods";
import {FormInterface} from "@/views/Admin/Events/Event/Promocodes/Form/Form.interface";
import JSONAPI from "@/utils/JSONAPI/JSONAPI";
import {PromocodesServiceInterface} from "@/services/PromocodesService/PromocodesService.interface";
import {PromocodeGroupsServiceInterface} from "@/services/PromocodeGroupsService/PromocodeGroupsService.interface";

const getPromocodeResource = async ({params}) => {
    let response, data

    const url = `${API.getPromocodeList.path(params)}`;
    const method = API.getPromocodeList.method;

    try {
        response = await postData(null, url, method, null);
        data = await deserializedData(response.data);
    } catch (e) {
        console.error(e);
    }

    return camelcaseKeys({data}, {deep: true});
}

type CreatePromocodeArgs = {
    data: FormInterface;
    typeEvent?: 'events' | 'courses' | 'subscribes';
};

export const createPromocodeResource = async ({data, typeEvent}: CreatePromocodeArgs) => {
    const flatForm = serializeFormValues(data);
    const schema = JSONAPI.inferSchemaFromSample(flatForm);
    const requestData = JSONAPI.serialize('promocodes', flatForm, schema);

    requestData.data.relationships = requestData.data.relationships || {};
    const eventId = flatForm.eventId;
    if (eventId) {
        requestData.data.relationships.event = {
            data: {id: String(eventId), type: typeEvent},
        };
    }

    const raw = data.fields.groupId?.value ?? flatForm.groupId;
    const fromVariant = data.fields.groupId?.variants?.[raw]?.id;
    const resolvedGroupId =
        fromVariant ?? (/^\d+$/.test(String(raw)) ? String(raw) : '');

    if (resolvedGroupId) {
        requestData.data.relationships['promocode-group'] = {
            data: {id: resolvedGroupId, type: 'promocode-groups'},
        };
    }

    return await postData(
        requestData,
        API.createPromocode.path,
        API.createPromocode.method,
        null
    );
};

export const updatePromocodeResource = async ({data, id, typeEvent}: {
    data: FormInterface,
    id: string,
    typeEvent: string
}) => {
    const flatForm = serializeFormValues(data);

    delete flatForm.groupId;
    delete flatForm.eventId;

    const schema = JSONAPI.inferSchemaFromSample(flatForm);
    const requestData = JSONAPI.serialize('promocodes', flatForm, schema);

    requestData.data.id = id;
    requestData.data.relationships = requestData.data.relationships || {};

    const eventId = data.fields.eventId.value;
    if (eventId) {
        requestData.data.relationships.event = {
            data: {id: String(eventId), type: typeEvent},
        };
    }

    const raw = data.fields.groupId.value;
    const fromVariant = data.fields.groupId.variants?.[raw]?.id;
    const resolvedGroupId =
        fromVariant ?? (/^\d+$/.test(String(raw)) ? String(raw) : '');

    if (resolvedGroupId) {
        requestData.data.relationships['promocode-group'] = {
            data: {id: resolvedGroupId, type: 'promocode-groups'},
        };
    }

    return await postData(
        requestData,
        API.updatePromocode.path(id),
        API.updatePromocode.method,
        null
    );
};

const deletePromocodeResource = async (data: Partial<PromocodesServiceInterface | PromocodeGroupsServiceInterface>) => {
    const url = API.deletePromocode.path(data.id);
    const method = API.deletePromocode.method;

    return await postData(null, url, method, null);
}

const createGroupResource = async (name: string, eventId: number, ownerId: number, typeEvent: string) => {
    const requestData = {
        data: {
            type: "promocode-groups",
            attributes: {
                name,
                owner_id: String(ownerId),
                event_id: String(eventId),
            },
            relationships: {
                event: {
                    data: {
                        id: String(eventId),
                        type: typeEvent
                    }
                }
            }
        }
    };

    return await postData(
        requestData,
        API.createPromocodeGroups.path,
        API.createPromocodeGroups.method,
        null
    );
};

const updateGroupResource = async (name: string, eventId: number, ownerId: number, groupId: number) => {
    const requestData = {
        data: {
            type: "promocode-groups",
            id: String(groupId),
            attributes: {
                name,
                owner_id: String(ownerId),
            }
        }
    };

    return await postData(
        requestData,
        API.updatePromocodeGroup.path(groupId),
        API.updatePromocodeGroup.method,
        null
    );
};

const getUserResource = async () => {
    const url = API.getUserData.path;
    const method = API.getUserData.method;
    const response = await postData(null, url, method, null)
    const data = await deserializedData(response.data);

    return data
}


export {
    getPromocodeResource,
    createPromocodeResource,
    updatePromocodeResource,
    deletePromocodeResource,
    createGroupResource,
    updateGroupResource,
    getUserResource
}
